import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { find, isEmpty, last, propEq, split } from 'ramda';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Pagination from '@components/@core/Pagination';
import CommunityBlogs from '@components/BlogFeed/CommunityBlogs';
import CommunitySlider from '@components/CommunitySlider';
import DepartmentForum from '@components/DepartmentForum';
import FeaturedDepartments from '@components/FeaturedDepartments';
import PremiumBlogs from '@components/BlogFeed/PremiumBlogs';
import RecentDepartment from '@components/RecentDepartment';
import RecentForum from '@components/RecentForum';
import AppActions from '~/actions/app';
import CommunityActions from '~/actions/community';
import CompanyActions from '~/actions/company';
import VendorActions from '~/actions/vendor';
import CommunityHeader from '~/views/Community/Community/Header';

class Home extends Component {
    constructor(props) {
        super(props);
        const routes = split('/', props.location.pathname);
        const slug = last(routes);
        const department = find(propEq('slug', slug), props.departments);
        this.state = { department };
    }

    componentDidUpdate(prevProps, prevState) {
        const routes = split('/', this.props.location.pathname);
        const slug = last(routes);
        if (prevState.department.slug !== slug) {
            const department = find(propEq('slug', slug), this.props.departments);
            this.setState({ department });
            this.fetchData();
        }
    }

    componentDidMount() {
        this.fetchData();
        this.props.getDepartments();
        this.props.getCategories();
    }

    fetchData = () => {
        const { department } = this.state;
        const { authenticated } = this.props;

        this.props.getBlogPosts({ categoryId: department.id, exclusive: 1, perPage: 9, page: 1 }, 'premium');
        this.props.getBlogPosts({ categoryId: department.id, exclusive: 0, perPage: 9, page: 1 }, 'community');

        if (authenticated) {
            this.props.getBlogs({
                userId: this.props.user?.community_user?.id,
                slug: this.props.user?.community_user?.entity?.slug,
            });
            this.props.getEditFeaturedItems('post', { category_id: department.id });
        } else {
            this.props.getFeaturedItems();
        }
        this.props.getTopics({ category_id: department.id, order: 'recent' });
    };

    handleMenu = (event, item, page, perPage) => {
        if (event === 'edit') {
            this.props.toggleModal({
                type: 'Add Forum',
                data: { before: { topic: item, event, page, perPage } },
            });
        }
    };

    handleProductDetail = (product, categoryId) => {
        const { parent } = product;
        if (parent.business_id) {
            const payload = { id: parent.business_id };
            const route = `/business/${product.slug}?id=${parent.business_id}&category=${categoryId}`;
            this.props.getBusiness(payload, route);
        } else {
            toast.error('This product does not have business yet. Please reach out to Brandon!', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
        }
    };

    handlePagination = (page, perPage = 10) => {
        this.props.getBlogPosts({ categoryId: 10, exclusive: 1, page, perPage });
    };

    render() {
        const { blogs, departments, featuredItems, userRole, userName } = this.props;
        const { department } = this.state;
        const topics = this.props.topics[department.id];
        const premiumPosts = blogs.premium?.posts?.data || [];
        const communityPosts = blogs.community?.posts?.data || [];

        return (
            <ErrorBoundary className="departments-home">
                <Helmet>
                    <title>Dealership {department.name} blogs, forums & networking | DrivingSales</title>
                    <meta name="description" content={`Dealership ${department.name} blogs, forums & networking | DrivingSales`} />
                </Helmet>
                <CommunityHeader activeRoute={department.name} />
                <CommunitySlider title={department.name} data={featuredItems} />
                <div className="py-0 px-3 px-md-0">
                    <div className="row">
                        <div className="col-md-6">
                            <FeaturedDepartments
                                loading={false}
                                title="Featured CRM"
                                departments={departments}
                                onProductDetail={(product, categoryId) => this.handleProductDetail(product, categoryId)}
                            />
                            <PremiumBlogs loading={false} data={premiumPosts} title="Premium Blogs" />
                            <CommunityBlogs loading={false} data={communityPosts} title="Community Blogs" />
                            <Pagination
                                current={blogs.community?.posts?.current_page}
                                pers={[]}
                                per={blogs.community?.posts?.per_page}
                                total={blogs.community?.posts?.last_page}
                                onChange={(e) => this.handlePagination(e)}
                            />
                        </div>
                        <div className="col-md-6">
                            <RecentForum
                                loading={false}
                                title="Forum Activity"
                                recentTopic={topics?.topics?.data || []}
                                displayMenu
                                userRole={userRole}
                                userName={userName}
                                page={1}
                                perPage={10}
                                onMenu={this.handleMenu}
                            />
                            <DepartmentForum loading={false} title="Department Forums" departments={departments} />
                            {departments.map((department, index) => (
                                <RecentDepartment key={index} department={department} />
                            ))}
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        );
    }
}

Home.propTypes = {
    authenticated: PropTypes.bool,
    user: PropTypes.shape({ id: PropTypes.number }),
    userName: PropTypes.string,
    userRole: PropTypes.number,
    departments: PropTypes.array,
    featuredItems: PropTypes.array,
};

Home.defaultProps = {
    authenticated: false,
    user: { id: 0 },
    userName: '',
    userRole: 5,
    departments: [],
    featuredItems: [],
};

const mapStateToProps = (state) => ({
    authenticated: !isEmpty(state.app.token),
    user: state.app.user,
    userName: state.app.user_name,
    userRole: state.app.app_role_id,
    departments: state.company.blogCategories,
    featuredItems: state.community.featuredItems,
    blogs: state.community.blogs,
    topics: state.community.topics,
});

const mapDispatchToProps = (dispatch) => ({
    getBlogs: (payload) => dispatch(CommunityActions.getcommunityblogRequest(payload)),
    getBlogPosts: (e, type) => dispatch(CommunityActions.getcomblogpostsRequest(e, type)),
    getBusiness: (payload, route) => dispatch(CompanyActions.getbusinessRequest(payload, route)),
    getCategories: () => dispatch(VendorActions.getcategoriesRequest({ isCategory: true })),
    getDepartments: () => dispatch(CompanyActions.getblogcategoriesRequest()),
    getEditFeaturedItems: (type, e) => dispatch(CommunityActions.geteditfeatureditemsRequest(type, e, null, false)),
    getFeaturedItems: () => dispatch(CommunityActions.getfeatureditemsRequest()),
    getTopics: (e) => dispatch(CommunityActions.gettopicsRequest(e, false)),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Home);
